import { useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getSimpleImageUrl } from 'lib/image/imageUtils'

interface NavSection {
  label: string
  href: string
}

function getNavSections(
  regionCode: string,
  eventId: string,
  intl: IntlShape,
): Array<NavSection> {
  const base = `/${regionCode}/corporate-events/${eventId}`
  return [
    {
      label: intl.formatMessage({
        id: 'Vl-e-WQ_DOFUz-HZBUds',
        defaultMessage: 'Home',
      }),
      href: base,
    },
    {
      label: intl.formatMessage({
        id: 'UTjtk16iZuD0AbfMj1wX',
        defaultMessage: 'Things to know',
      }),
      href: `${base}/things-to-know`,
    },
    {
      label: intl.formatMessage({
        id: 'ih-AiSMGMIapQOI_n6lW',
        defaultMessage: 'Group Transfers',
      }),
      href: `${base}/flights`,
    },
    {
      label: intl.formatMessage({
        id: 'Z-IQSsWfmVuM6OUO0SYG',
        defaultMessage: 'Accommodation',
      }),
      href: `${base}/accommodation`,
    },
    {
      label: intl.formatMessage({
        id: 'lLBjK4riqApmfiPe-Wh2',
        defaultMessage: 'Optional Activities',
      }),
      href: `${base}/other-activities`,
    },
    {
      label: intl.formatMessage({
        id: 'N41vpmYASeYplCX-4x9K',
        defaultMessage: 'FAQs and T&Cs',
      }),
      href: `${base}/faq`,
    },
  ]
}

const Nav = styled.nav`
  z-index: 100;
  width: 100%;
  background: ${(props) => props.theme.palette.neutral.default.eight};

  ${mediaQueryUp.desktop} {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(30px);
    border-bottom: ${rem(1)} solid
      ${(props) => props.theme.palette.neutral.default.five};
  }
`

const MobileTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${rem(8)} ${rem(12)};

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const MobileBottom = styled.div`
  display: flex;
  align-items: center;
  overflow-x: auto;
  padding: 0 ${rem(12)};
  border-bottom: ${rem(1)} solid
    ${(props) => props.theme.palette.neutral.default.five};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const DesktopInner = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-block: ${rem(18)};
    padding-inline: ${rem(20)} ${rem(32)};
  }
`

const MobileLogo = styled.img`
  max-width: ${rem(162)};
  height: auto;
  object-fit: contain;
  display: block;
`

const DesktopLogo = styled.img`
  height: ${rem(36)};
  width: auto;
  display: block;
`

const MobileNavLink = styled.a`
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(20)};
  color: ${(props) => props.theme.palette.neutral.default.one};
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  padding: ${rem(10)} ${rem(12)};

  &.is-first {
    padding-inline-start: 0;
  }
`

const DesktopNavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
`

const DesktopNavLink = styled.a`
  font-size: ${rem(16)};
  font-weight: 400;
  line-height: ${rem(24)};
  color: ${(props) => props.theme.palette.neutral.default.one};
  text-decoration: underline;
  text-decoration-color: ${(props) => props.theme.palette.neutral.default.one};
  text-decoration-thickness: ${rem(1)};
  text-underline-offset: ${rem(6)};
  white-space: nowrap;
  padding: ${rem(12)} ${rem(16)};

  &.is-active {
    color: #666d82;
    text-decoration-color: #666d82;
  }
`

interface Props {
  eventId: string
  regionCode: string
}

function CorporateEventNavbar({ eventId, regionCode }: Props) {
  const intl = useIntl()
  const { pathname } = useLocation()
  const sections = useMemo(() => getNavSections(regionCode, eventId, intl), [eventId, intl, regionCode])
  return (
    <Nav>
      <MobileTop>
        <MobileLogo
          src={getSimpleImageUrl('cplg5940koezrayr6js3', 'png')}
          alt="7Platinum"
        />
      </MobileTop>

      <MobileBottom>
        {sections.map((section, i) => (
          <MobileNavLink
            key={section.href}
            href={section.href}
            className={i === 0 ? 'is-first' : undefined}
          >
            {section.label}
          </MobileNavLink>
        ))}
      </MobileBottom>

      <DesktopInner>
        <DesktopLogo
          src={getSimpleImageUrl('cplg5940koezrayr6js3', 'png')}
          alt="7Platinum"
        />
        <DesktopNavLinks>
          {sections.map((section) => (
            <DesktopNavLink
              key={section.href}
              href={section.href}
              className={pathname === section.href ? 'is-active' : undefined}
            >
              {section.label}
            </DesktopNavLink>
          ))}
        </DesktopNavLinks>
      </DesktopInner>
    </Nav>
  )
}

export default CorporateEventNavbar
